import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * The scoped screens (Ringkasan, Rilis, Build, Analitik) keep their app and
 * channel in the query string instead of component state. The shell's scope
 * switcher and the top-bar search write the same two parameters, so the
 * sidebar and the page can never disagree about what is being looked at, and
 * a filtered view stays linkable — an operator can paste the URL of the exact
 * table they are staring at into a chat.
 */
export type ScopePatch = Record<string, string | number | undefined>;

export interface Scope {
  /** App slug; "" until the first app has loaded and written itself in. */
  app: string;
  /** Channel name; "" means every channel of the app. */
  channel: string;
  /** Free-text term from the top-bar search; "" when the box is empty. */
  q: string;
  get: (key: string) => string;
  /**
   * Writes a patch onto the current query string. An empty string or
   * `undefined` removes the key rather than leaving `?platform=` behind, which
   * keeps a default view's URL clean.
   */
  set: (patch: ScopePatch, options?: { replace?: boolean }) => void;
}

export function useScope(): Scope {
  const [params, setParams] = useSearchParams();

  const set = useCallback(
    (patch: ScopePatch, options?: { replace?: boolean }) => {
      // Functional update, never a snapshot of `params`: two controls changed
      // in the same tick (app + channel reset) must not clobber each other.
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(patch)) {
            const text = value === undefined ? "" : String(value);
            if (text === "") next.delete(key);
            else next.set(key, text);
          }
          return next;
        },
        { replace: options?.replace ?? false },
      );
    },
    [setParams],
  );

  const get = useCallback((key: string) => params.get(key) ?? "", [params]);

  return useMemo(
    () => ({ app: get("app"), channel: get("channel"), q: get("q"), get, set }),
    [get, set],
  );
}

/** Case-insensitive "does any of these fields contain the term" for `?q=`. */
export function matchesTerm(term: string, ...fields: Array<string | number | null>): boolean {
  if (!term) return true;
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return fields.some((field) => field !== null && String(field).toLowerCase().includes(needle));
}
